import { useState } from 'react';
import {
  addSectionComment,
  ReviewComment,
  ReviewSectionConversation,
} from '../../models/ContentReviewInterface';
import { ReviewContentEditViewProps } from './reviewContentEditViewTypes';

interface UseReviewCommentFlowsParams {
  reviewData: ReviewContentEditViewProps['reviewData'];
  onReviewDataUpdate: ReviewContentEditViewProps['onReviewDataUpdate'];
  currentUser: ReviewContentEditViewProps['currentUser'];
  setSubmitting: (submitting: boolean) => void;
}

/**
 * Comment-only review flows (standalone section comments + minimal inline replies).
 * Extracted from useReviewContentEditView so the content-editing hook stays focused
 * on edit/add/replace orchestration. Shares submit state via setSubmitting.
 */
export function useReviewCommentFlows({
  reviewData,
  onReviewDataUpdate,
  currentUser,
  setSubmitting,
}: UseReviewCommentFlowsParams) {
  const [showAddCommentDialog, setShowAddCommentDialog] = useState(false);
  const [addCommentSectionId, setAddCommentSectionId] = useState<string | null>(null);
  const [addCommentText, setAddCommentText] = useState('');
  const [showMinimalReply, setShowMinimalReply] = useState<string | null>(null);
  const [minimalReplyText, setMinimalReplyText] = useState('');

  const handleAddCommentSubmit = async () => {
    if (!addCommentText.trim() || !addCommentSectionId) {
      return;
    }

    setSubmitting(true);
    try {
      const newConversation = {
        conversationId: `conversation-${Date.now()}`,
        sectionId: addCommentSectionId,
        comments: [
          {
            message: addCommentText,
            submittedBy: { id: currentUser.id, name: currentUser.name },
            createdAt: new Date().toISOString(),
          } as ReviewComment,
        ],
      };

      const updatedReviewData = addSectionComment(reviewData, newConversation);
      await onReviewDataUpdate(updatedReviewData);

      setShowAddCommentDialog(false);
      setAddCommentText('');
      setAddCommentSectionId(null);
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCommentCancel = () => {
    setShowAddCommentDialog(false);
    setAddCommentText('');
    setAddCommentSectionId(null);
  };

  const handleMinimalReplySubmit = async (sectionId: string) => {
    if (!minimalReplyText.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      const newConversation: ReviewSectionConversation = {
        conversationId: `conv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        sectionId,
        comments: [
          {
            message: minimalReplyText,
            submittedBy: {
              id: currentUser.id,
              name: currentUser.name || 'Unknown',
            },
            createdAt: new Date().toISOString(),
          },
        ],
      };

      const updatedReviewData = addSectionComment(reviewData, newConversation);
      await onReviewDataUpdate(updatedReviewData);
      setShowMinimalReply(null);
      setMinimalReplyText('');
    } catch (error) {
      console.error('Error adding minimal reply:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMinimalReplyCancel = () => {
    setShowMinimalReply(null);
    setMinimalReplyText('');
  };

  return {
    showAddCommentDialog,
    addCommentSectionId,
    addCommentText,
    showMinimalReply,
    minimalReplyText,
    setShowAddCommentDialog,
    setAddCommentSectionId,
    setAddCommentText,
    setShowMinimalReply,
    setMinimalReplyText,
    handleAddCommentSubmit,
    handleAddCommentCancel,
    handleMinimalReplySubmit,
    handleMinimalReplyCancel,
  };
}
